import { useState } from 'react';
import { Quest, QUEST_STATUS } from '@olagg/db-types';
import { Modal, ModalOverlay, ModalContent, ModalCloseButton, ModalBody, Heading, Box, Flex, Spinner, Button } from '@chakra-ui/react';
import TwitterTimelineEmbed from '../../Twitter/TwitterTimelineEmbed'
import TwitterTweetEmbed from '../../Twitter/TwitterTweetEmbed'
import { fetcher, QuestEndpoints } from '@olagg/api-hooks';


type IProps = {
    quest: Quest;
    onClose: () => void;
    isOpen: boolean;
};

const DisplaySelector = ({ username, subtype, tweetId, text, handleSubmit, buttonText }) => {
    const [verifying, setVerifying] = useState(false);

    const onLoad = (event = null) => (tweetWidgetEl: HTMLDivElement) => {
        if (event) window.twttr.events.bind(event, () => handleClick()());
    }

    const handleClick = (event = null) => () => {
        if (event) {
            const url = `https://twitter.com/intent/${event}`
            window.open(url, '_blank').focus();
        }
        setVerifying(true);
        handleSubmit()
    };

    switch (subtype) {
        case 'like':
            return (
                <Box w={{base: '100%', md: 'auto'}}>
                    <TwitterTweetEmbed tweetId={tweetId} onLoad={onLoad('like')} options={{
                        cards: 'hidden',
                        hide_thread: true,
                        'data-conversation': 'none',
                    }} />
                    {!verifying && <Button
                        onClick={handleClick(`like?tweet_id=${tweetId}`)}
                        backgroundColor={'black'}
                        w={'100%'}
                        color={'white'}
                        _hover={{
                            backgroundColor: 'black.600'
                        }}
                    >
                        {buttonText || 'Dar Me Gusta'}
                    </Button>}
                    {verifying && <Button disabled backgroundColor={'gray.800'} w={'100%'} color={'white'}>
                        <Spinner size='sm' color='white' mr={2} />
                        Validando...
                    </Button>}
                </Box>
            );
        case 'follow':
            return (
                <Box w={{base: '100%', md: 'auto'}}>
                    <TwitterTimelineEmbed
                        sourceType="profile"
                        screenName={username}
                        noHeader
                        tweetLimit={1}
                        options={{ height: 300, cards: 'hidden' }}
                        onLoad={onLoad}
                    />
                    <Box mt={4}>
                        {!verifying && <Button
                            onClick={handleClick(`follow?screen_name=${username}`)}
                            backgroundColor={'black'}
                            w={'100%'}
                            color={'white'}
                            _hover={{
                                backgroundColor: 'black.600'
                            }}
                        >
                            {buttonText || 'Seguir cuenta'}
                        </Button>}
                        {verifying && <Button disabled backgroundColor={'gray.800'} w={'100%'} color={'white'}>
                            <Spinner size='sm' color='white' mr={2} />
                            Validando...
                        </Button>}
                    </Box>
                </Box>
            );
        case 'retweet':
            return (
                <Box textAlign={'center'}>
                    <TwitterTweetEmbed tweetId={tweetId} onLoad={onLoad} options={{
                        cards: 'hidden',
                        hide_thread: true,
                        'data-conversation': 'none',
                    }} />
                    <Box mt={4}>
                        {!verifying && <Button
                            onClick={handleClick(`retweet?tweet_id=${tweetId}`)}
                            backgroundColor={'black'}
                            w={'100%'}
                            color={'white'}
                            _hover={{
                                backgroundColor: 'black.600'
                            }}
                        >
                            {buttonText || 'Retweet'}
                        </Button>}
                        {verifying && <Button disabled backgroundColor={'gray.800'} w={'100%'} color={'white'}>
                            <Spinner size='sm' color='white' mr={2} />
                            Validando...
                        </Button>}
                    </Box>
                </Box>
            );
        case 'tweet':
            return (
                <Box w={{base: '100%', md: 'auto'}}>
                    {!verifying && <Button
                        onClick={handleClick(`tweet?text=${text}`)}
                        backgroundColor={'black'}
                        w={'100%'}
                        color={'white'}
                        _hover={{
                            backgroundColor: 'black.600'
                        }}
                    >
                        {buttonText || 'Escribir post'}
                    </Button>}
                    {verifying && <Button disabled backgroundColor={'gray.800'} w={'100%'} color={'white'}>
                        <Spinner size='sm' color='white' mr={2} />
                        Validando...
                    </Button>}
                </Box>
            );
        default:
            return (
                <Heading>Quest subtype not recognized</Heading>
            );
    }
};

const CompletedQuest = ({ quest }: { quest: Quest }) => {
    const backToQuests = () => {
        if (window.location.pathname === '/quests') {
            window.location.reload();
        } else {
            window.location.href = '/quests';
        }
    }

    return (
        <Box w={'100%'}>
            <Box backgroundColor={'gray.100'} p={5} w={'100%'} borderRadius={'lg'} mb={4}>
                <Heading
                    fontSize={'38px'}
                    color={'black'}
                    pb={4}
                    textAlign={'center'}
                >
                    + {quest.score} XP
                </Heading>
                <Box textAlign={'center'} fontWeight={'semibold'}>
                    ¡Felicitaciones!
                </Box>
            </Box>
            <Button
                backgroundColor={'black'}
                w={'100%'}
                color={'white'}
                _hover={{
                    backgroundColor: 'black.200'
                }}
                onClick={backToQuests}
            >
                Volver a misiones
            </Button>
        </Box>
    );
};

const ModalTwitterQuest = ({ quest, onClose, isOpen }: IProps) => {
    const lang = localStorage.getItem('i18n_lang') || 'es';
    const [questStatus, setQuestStatus] = useState(quest.status);
    const subtype = quest.extraData.subtype;
    const text = lang === 'es' ? quest.extraData.tweetText : quest.extraData?.[lang]?.tweetText;
    const buttonText = lang === 'es' ? quest.extraData.modalButtonText : quest.extraData?.[lang]?.modalButtonText;
    const tweetId = quest.extraData.tweetId;
    const username = quest.extraData.username;
    const title = lang === 'es' ? quest.title : quest.extraData?.[lang]?.title;
    const description = lang === 'es' ? quest.description : quest.extraData?.[lang]?.description;
    const submit = () => fetcher(QuestEndpoints.completeTwitterQuest(quest.id))
        .then(() => setQuestStatus(QUEST_STATUS.COMPLETED))

    const handleClose = () => {
        onClose();
        if (questStatus === QUEST_STATUS.COMPLETED) {
            window.location.reload();
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={handleClose} isCentered size={{base: 'sm', md: '2xl'}}>
            <ModalOverlay />
            <ModalContent>
                <ModalCloseButton />
                <ModalBody py={8}>
                    <Heading
                        fontSize={'18px'}
                        color={'olaggPurple.100'}
                        pb={4}
                        textAlign={'center'}
                        borderBottom='1px solid rgba(75,75,75,0.3)'
                    >
                        {title}
                    </Heading>
                    <Box p={4} textAlign={'center'} fontWeight={'semibold'}>
                        {description}
                    </Box>
                    <Flex justifyContent={'center'}>
                        {questStatus === QUEST_STATUS.COMPLETED && <CompletedQuest quest={quest} />}
                        {questStatus === QUEST_STATUS.PENDING && <DisplaySelector username={username} subtype={subtype} text={text} tweetId={tweetId} handleSubmit={submit} buttonText={buttonText} />}
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    )
};

export default ModalTwitterQuest;
